import axios, {
  AxiosError,
  AxiosInstance,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { camelCase } from 'change-case';
import * as Sentry from '@sentry/nextjs';

import { UnrestrictedEnpoints } from '@/core/constants/constants';

import { caseConverter } from '@/utils/case-converter';

const BACKEND_URL = process.env.SERVER_BACKEND_URL || process.env.NEXT_PUBLIC_BACKEND_URL;

export class Request {
  private instance!: AxiosInstance;

  constructor() {
    this.initConfig();
  }

  initConfig() {
    this.instance = axios.create({
      baseURL: (
        BACKEND_URL && (BACKEND_URL as string).endsWith('/')
      ) ?
        `${BACKEND_URL}api/` :
        `${BACKEND_URL}/api/`,
    });
    this.instance.interceptors.response.use(
      response => ({ ...response }),
      async error => {
        Sentry.captureException(error);

        if (!error.response) {
          return Promise.reject(new Error('Error do not have a response.'));
        }

        // Return any error which is not due to authentication back to the calling service
        if (error.response.status !== 401 ||
          Object.values(UnrestrictedEnpoints).includes(error.config.url)) {
          return Promise.reject(this.parseError(error));
        }

        return Promise.reject(this.parseError(error));
      }
    );
  }

  parseError(error: AxiosError): AxiosError {
    const { response } = error;

    const convertedData = caseConverter(response?.data || {}, camelCase);
    let data: unknown;

    if (typeof convertedData === 'string' || typeof convertedData === 'number' || typeof convertedData === 'boolean' || typeof convertedData === 'undefined') {
      data = convertedData;
    } else {
      const nonFieldErorrs: Array<string> = response?.data?.non_field_errors || [];

      data = {
        ...convertedData,
        _error: nonFieldErorrs,
      };
    }

    return {
      ...error,
      response: {
        ...response,
        data,
      } as AxiosResponse,
    };
  }

  get<P, QP = unknown>(
    url: string,
    config?: AxiosRequestConfig & { params?: QP },
    deepPopulate?: boolean
  ): AxiosPromise<P> {
    const strapiGetURL = deepPopulate ? `${url}?populate=deep` : url;

    return this.instance.get(strapiGetURL, config);
  }

  post<P>(url: string, data?: unknown, config?: AxiosRequestConfig): AxiosPromise<P> {
    return this.instance.post(url, data, config);
  }

  options<P>(url: string, config?: AxiosRequestConfig): AxiosPromise<P> {
    return this.instance.options(url, config);
  }

  patch<P>(url: string, data?: unknown, config?: AxiosRequestConfig): AxiosPromise<P> {
    return this.instance.patch(url, data, config);
  }

  put<P>(url: string, data?: unknown, config?: AxiosRequestConfig): AxiosPromise<P> {
    return this.instance.put(url, data, config);
  }

  delete<P>(url: string, config?: AxiosRequestConfig): AxiosPromise<P> {
    return this.instance.delete(url, config);
  }
}

export const request = new Request();
